import React, { useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import CurrentCustomer from 'context/global-customer-creation/context';
import type { DataCurrentCustomerType } from 'context/global-customer-creation/types';

import {
  createPhysicalPersonalData,
  updatePhysicalPersonalData,
  updatePhysicalEconomicInformation,
  createPhysicalBankAccount,
  updatePhysicalBankAccount,
  createPhysicalBeneficiary,
  updatePhysicalBeneficiary,
  updatePhysicalInvestmentAccount,
  updatePhysicalInvestmentAccountMoral,
  createCoOwner,
} from 'api/client';

import { PhysicalStepRequest } from 'api/client/models';

import { arrayValidation } from 'utils/array';
import { getDateByObject } from 'utils/date';

import type LabelValue from 'commons/modules/label-value';

import { clientRoles, PERSON_TYPE } from 'constants/globals';

import type { StepFieldsConfig } from 'components/NewClient/Onboarding/config';

import OnboardingSaveSteps from './context';
import {
  permanentResidencyValidation,
  generalDataRemapper,
  economicInfoDataRemapper,
  bankAccountRemapper,
} from './helpers';
import type { StepStatus } from './types';
import type {
  BankAccountValues,
  BeneficiaryAccountValues,
  CoOwnerValues,
  EconomicInformationValues,
  FeeValues,
  GeneralDataValues,
  InvestmentAccountItem,
  InvestmentAccountValues,
} from './models';

const initialState = { saved: false };
const initialStepGeneralState = {
  clientId: null,
  contractId: null,
  name: '',
  fathersLastName: '',
  mothersLastName: '',
  fullName: '',
  addresses: [],
  saved: false,
};

interface Props {
  children: React.ReactNode;
}

const OnboardingSaveStepsProvider = ({ children }: Props) => {
  const { pathname } = useLocation();
  const isMoralP = String(pathname).includes(`/clientes/moral/`);
  const { updateCurrentCustomer, currentCustomer } =
    useContext(CurrentCustomer);
  const [dataStepGeneral, setDataStepGeneral] = useState<GeneralDataValues>(
    initialStepGeneralState,
  );

  const [dataStepEconomicInfo, setDataStepEconomicInfo] =
    useState<EconomicInformationValues>(initialState);
  const [dataStepAccount, setDataStepAccount] =
    useState<BankAccountValues>(initialState);
  const [dataStepBeneficiaries, setDataStepBeneficiaries] =
    useState<BeneficiaryAccountValues>(initialState);
  const [dataStepInvestmentAccount, setDataStepInvestmentAccount] =
    useState<InvestmentAccountValues>(initialState as InvestmentAccountValues);

  const [dataStepCoOwner, setDataStepCoOwner] = useState<CoOwnerValues>(
    initialState as CoOwnerValues,
  );

  const [zipCodes, setZipCodes] = useState<LabelValue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [stepStatus, setStepStatus] = useState<StepStatus>({});

  const resetPhysicalPersonSteps = () => {
    setDataStepGeneral(initialStepGeneralState);
    setDataStepEconomicInfo(initialState);
    setDataStepAccount(initialState);
    setDataStepBeneficiaries(initialState);
    setDataStepInvestmentAccount(initialState as InvestmentAccountValues);
    setDataStepCoOwner(initialState as CoOwnerValues);
  };
  const resetSteps = () => {
    setStepStatus({});
  };

  const getBeneficiaryInitialValues = (fields: any) => ({
    beneficiaries: [
      {
        [fields.beneficiaryName.name]: '',
        [fields.beneficiaryFathersLastName.name]: '',
        [fields.beneficiaryMothersLastName.name]: '',
        [fields.percentage.name]: '',
        [fields.relationship.name]: '',
        [fields.curp.name]: '',
        [fields.rfc.name]: '',
        [fields.birthDate.name]: '',
        [fields.countryOfOrigin.name]: '',
        [fields.nationality.name]: '',
        [fields.areaStreet.name]: '',
        [fields.areaExteriorNumber.name]: '',
        [fields.areaInteriorNumber.name]: '',
        [fields.zipCode.name]: '',
        [fields.areaSuburb.name]: '',
        [fields.cveLocation.name]: '',
        [fields.areaCity.name]: '',
        [fields.areaState.name]: '',
        [fields.cvePais.name]: '',
        [fields.accountNumber.name]: '',
      },
    ],
  });

  const getInvestmentAccountInitialValues = (fields: any) => ({
    investmentAccounts: [
      {
        [fields.brokerageHouse.name]: '',
        [fields.accountNumber.name]: '',
        [fields.accountType.name]: '',
        [fields.serviceType.name]: '',
        [fields.status.name]: '',
        [fields.currencyType.name]: '',
        [fields.country.name]: '',
      },
    ],
  });

  const getValueAmount = (value: string | number | null | undefined | null) => {
    if (!value) return 0;
    const newNumber = `${value}`
      .replace('$', '')
      .replace('%', '')
      .replaceAll(',', '');
    return newNumber ? Number(newNumber) : 0;
  };

  const feeListRemapper = (fees: FeeValues[] = []) => {
    if (!arrayValidation(fees)) return [];
    const feeList = fees.map((item) => ({
      id: item.id || null,
      fee: item.fee?.value || '',
      paymentType: item.paymentType?.value || '',
      amountPercentage: getValueAmount(item.amount) || 0,
    }));
    return feeList;
  };

  const investmentAccountsRemapper = (
    investmentAccounts: InvestmentAccountItem[],
  ) => {
    return investmentAccounts.map((i) => ({
      ...i,
      accountId: i.accountId || null,
      accountNumber: i.accountNumber,
      accountType: i.accountType && i.accountType.value,
      brokerageHouse: i.brokerageHouse && i.brokerageHouse.value,
      currencyType: i.currencyType && i.currencyType.value,
      country: i.country && i.country.value,
      serviceType: i.serviceType && i.serviceType.value,
      status: i.status && i.status.value,
      openingDate: getDateByObject({
        date: i.openingDate,
        format: 'yyyy-MM-dd',
      }),
      cancellationDate: getDateByObject({
        date: i.cancellationDate,
        format: 'yyyy-MM-dd',
      }),
      feeList: feeListRemapper(i.fees),
    }));
  };

  const beneficiaryRemapper = (values: BeneficiaryAccountValues) => {
    return {
      holderId: dataStepGeneral.clientId,
      contractId: dataStepGeneral.contractId,
      beneficiaries:
        values.beneficiaries?.map((item: any) => ({
          ...item,
          birthDate: getDateByObject({
            date: item.birthDate,
            requiredReplaceSeparator: true,
          }),
          beneficiaryId: item.beneficiaryId || null,
          relationship: (item.relationship || '') && item.relationship.value,
          countryOfOrigin:
            (item.countryOfOrigin || '') && item.countryOfOrigin.label,
          nationality: (item.nationality || '') && item.nationality.label,
          areaSuburb: (item.areaSuburb || '') && item.areaSuburb.label,
          areaState: (item.areaState || '') && item.areaState.label,
          cveState:
            item.areaState && typeof item.areaState === 'object'
              ? item.areaState.value
              : item.areaState || '',
          cvePais: (item.cvePais || '') && item.cvePais.label,
          investmentAccountList: arrayValidation(item.investmentAccountList)
            ? item.investmentAccountList.map((account: any) => ({
                investmentAccount: account.value,
              }))
            : [],
        })) || [],
    };
  };

  const saveStepGeneralData = async (
    values: GeneralDataValues,
    clientRole: string,
  ) => {
    const permanentResidency = permanentResidencyValidation(values);
    const finalValues = generalDataRemapper(
      values,
      permanentResidency,
      clientRole,
      dataStepGeneral,
    );

    if (clientRole === clientRoles.CO_OWNER)
      finalValues.cveParentesco =
        values.cveParentesco && values.cveParentesco.value;

    const persistGeneralData = () => {
      if (clientRole !== clientRoles.CLIENT) return createCoOwner(finalValues);
      return dataStepGeneral.saved
        ? updatePhysicalPersonalData(finalValues)
        : createPhysicalPersonalData(finalValues);
    };

    const { data, success } = await persistGeneralData();

    if (success && data) {
      const {
        name = '',
        fathersLastName = '',
        mothersLastName = '',
      } = (data.generalData ?? {}) as Record<string, unknown>;
      const fullName = `${name} ${fathersLastName} ${mothersLastName}`;
      if (clientRole === clientRoles.CO_OWNER) {
        setDataStepGeneral({
          clientId: dataStepGeneral.clientId,
          contractId: dataStepGeneral.contractId,
          coOwner: { ...finalValues, ...data },
        } as GeneralDataValues);
        updateCurrentCustomer({
          coOwner: { ...data },
        } as DataCurrentCustomerType);
      } else if (clientRole === clientRoles.CLIENT) {
        setDataStepGeneral({
          ...finalValues,
          clientId: data.clientId,
          coOwner: null,
          contractId: data.contractId,
          saved: true,
        });
        updateCurrentCustomer({
          ...data,
          fullName,
          taxPersonType: PERSON_TYPE.fisica,
        } as unknown as DataCurrentCustomerType);
      }
      return { success };
    }
    return { success: false };
  };

  const saveStepEconomicInfo = async (
    values: EconomicInformationValues,
    clientRole: string,
    fieldsConfig: StepFieldsConfig<'fisica', 'economicInfo'>,
  ) => {
    const finalValues: any = economicInfoDataRemapper(
      values,
      dataStepGeneral,
      clientRole,
      fieldsConfig,
    );

    delete finalValues.registrationDate;
    delete finalValues.updateDate;

    const saveStepResponse =
      await updatePhysicalEconomicInformation(finalValues);

    if (saveStepResponse.success) {
      setDataStepEconomicInfo({
        ...finalValues,
        ...saveStepResponse,
        saved: true,
      } as EconomicInformationValues);
      return { success: true };
    }
    return { success: false };
  };

  const saveStepBankAccount = async (
    values: BankAccountValues,
    clientRole: string,
  ) => {
    const finalValues = bankAccountRemapper(
      values,
      dataStepGeneral,
      clientRole,
    );

    const saveStepResponse = dataStepAccount.saved
      ? await updatePhysicalBankAccount(finalValues)
      : await createPhysicalBankAccount(finalValues);

    if (saveStepResponse.success) {
      setDataStepAccount({ ...finalValues, ...saveStepResponse, saved: true });
      updateCurrentCustomer({
        onboardingComplete: true,
      } as DataCurrentCustomerType);
      return { success: true };
    }
    return { success: false, error: saveStepResponse };
  };

  const saveStepBeneficiary = async (values: BeneficiaryAccountValues) => {
    const finalValues = beneficiaryRemapper(
      values,
    ) as unknown as PhysicalStepRequest;

    const saveStepResponse = dataStepBeneficiaries.saved
      ? await updatePhysicalBeneficiary(finalValues)
      : await createPhysicalBeneficiary(finalValues);

    if (saveStepResponse.success) {
      setDataStepBeneficiaries({
        ...saveStepResponse,
        ...finalValues,
        saved: true,
      });
      updateCurrentCustomer({
        onboardingComplete: true,
      } as DataCurrentCustomerType);
      return { success: true };
    }
    return { success: false, error: saveStepResponse };
  };

  const handleSaveInvestmentAccounts = async (
    investmentAccounts: InvestmentAccountItem[],
  ) => {
    const values = investmentAccountsRemapper(investmentAccounts);
    const body = {
      clientId: currentCustomer.clientId,
      contractId: currentCustomer.contractId,
      investmentAccounts: values,
    };

    const { success, errorMessage } = isMoralP
      ? await updatePhysicalInvestmentAccountMoral(body)
      : await updatePhysicalInvestmentAccount(body);

    if (success) {
      updateCurrentCustomer({
        accountsComplete: true,
      } as DataCurrentCustomerType);
    }
    return { success, errorMessage };
  };

  return (
    <OnboardingSaveSteps.Provider
      value={{
        dataStepAccount,
        dataStepBeneficiaries,
        dataStepEconomicInfo,
        dataStepGeneral,
        dataStepInvestmentAccount,
        getBeneficiaryInitialValues,
        getInvestmentAccountInitialValues,
        handleSaveInvestmentAccounts,
        loading,
        resetPhysicalPersonSteps,
        saveStepBankAccount,
        saveStepBeneficiary,
        saveStepEconomicInfo,
        saveStepGeneralData,
        setDataStepAccount,
        setDataStepBeneficiaries,
        setDataStepEconomicInfo,
        setDataStepGeneral,
        setDataStepInvestmentAccount,
        setLoading,
        setZipCodes,
        zipCodes,
        stepStatus,
        setStepStatus,
        resetSteps,
        dataStepCoOwner,
        setDataStepCoOwner,
      }}
    >
      {children}
    </OnboardingSaveSteps.Provider>
  );
};

export default OnboardingSaveStepsProvider;
